/**
 * Auth utilities for token validation (no signature verification; backend validates).
 * Used to avoid sending expired tokens. All decoding is best-effort with try/catch.
 */

const EXPIRY_BUFFER_SECONDS = 30;

function readStoredUser() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Decodes base64url to string (JWT payload is base64url-encoded).
 * Does not throw; returns null on failure.
 */
function base64UrlDecode(str) {
    try {
        if (!str || typeof str !== 'string') return null;
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad) base64 += '===='.slice(0, 4 - pad);
        return atob(base64);
    } catch {
        return null;
    }
}

/**
 * Reads token from storage (localStorage 'user' -> user.data.token), decodes JWT payload,
 * checks exp (with optional buffer). Returns token string if valid, null otherwise.
 * Never throws; malformed or expired tokens return null.
 */
export function getValidToken() {
    try {
        const user = readStoredUser();
        if (!user) return null;
        const token = user?.data?.token;
        if (!token || typeof token !== 'string') return null;

        const parts = token.split('.');
        if (parts.length < 2) return null;

        const decoded = base64UrlDecode(parts[1]);
        if (!decoded) return null;

        const payload = JSON.parse(decoded);
        const exp = payload?.exp;
        if (exp == null || typeof exp !== 'number') return null;

        const nowSeconds = Date.now() / 1000;
        if (exp < nowSeconds + EXPIRY_BUFFER_SECONDS) return null;

        return token;
    } catch {
        return null;
    }
}

export function hasValidSession() {
    return getValidToken() !== null;
}

export function getStoredUserWithValidSession() {
    const user = readStoredUser();
    if (!user) return null;

    return hasValidSession() ? user : null;
}

export function clearStoredUserIfSessionInvalid() {
    const user = readStoredUser();
    if (!user) return false;
    if (hasValidSession()) return false;

    localStorage.removeItem('user');
    localStorage.removeItem('adMessageDisplayed');
    return true;
}
